<template lang="pug">
.screen-blinds.flex.flex-col.gap-12.lg_flex-row.lg_gap-0.w-full.fade-in(
  :data-completed="suspend.completed[screen.id] || ''"
  :class="{ 'mobile-layout': !largeDevice }"
)
  uc-preload-sfx(name="blinds-open")
  .blinds-item.relative.shadow.rounded-xl.lg_rounded-none.group(
    :class="[i === activeCard ? 'lg_flex-1 lg_w-40' : 'lg_flex-0 lg_w-20', { 'lg_hover_lg_w-40': activeCard !== i, dark: card.dark }, { 'transition-all duration-500 ease-out': !suspend.reducedMotion }, { 'ltr_border-l-2 rtl_border-r-2 border-white': i > 0 }]"
    v-for="(card, i) in screen.cards"
  )
    .absolute.text-4xl.text-center.w-full.opacity-0.scale-100.hidden.lg_block.pointer-events-none.z-20(
      :class="[{ 'lg_opacity-90 lg_transition lg_group-hover_opacity-100 lg_group-hover_scale-125': activeCard !== i }]"
      class="top-[45%] text-white/60"
      uc-word-skip=""
    )
      uc-icon(icon="theme:plus-circle")

    .overflow-hidden.w-full.h-full.bg-gray-200.transition.relative.rounded-xl.lg_rounded-none.z-10(
      :class="[{ 'ltr_lg_rounded-l-xl rtl_lg_rounded-r-xl': i === 0, 'ltr_lg_rounded-r-xl rtl_rounded-l-xl': i === screen.cards.length - 1 }, i === activeCard ? 'lg_brightness-100' : 'lg_brightness-50', { 'lg_group-hover_brightness-100': activeCard !== i }]"
    )
      .p-8.md_px-16.md_pe-0.w-full(class="md_w-[60%] lg_w-[500px]")
        .absolute.inset-0.z-20.hidden.lg_inline.pointer-events-none.text-white.text-left.text-2xl.pt-1.ps-2(
          uc-word-skip=""
          v-if="suspend.completed[getCardId(i)]"
        )
          uc-icon(icon="theme:check-circle")

        uc-asset.fill.min-w-full.h-full.object-cover.z-1(
          uc-word-skip=""
          :size="16"
          :src="card.bg"
        )
        .transition.z-10.relative.lg_auto-alpha-0.lg_delay-100(
          :class="[{ 'lg_auto-alpha-1': activeCard === i }, { 'bg-paper p-6 rounded-lg': card?.panel }]"
        )
          audio-marker(:name="getCardId(i)")
          .blinds-card(
            :aria-hidden="!audioDoc && !wordDoc && activeCard !== i"
          )
            uc-content(
              :editPath="`${editPath}.cards.${i}`" 
              :screen="card"
            )

    //- expose these strings for the translate script
    //- text="Open card ${i} of ${n}"
    .button-container.hidden.lg_block
      button.fill.focus-visible_ring-4.z-20.rounded-xl.w-full(
        :aria-label="ariaLabel(i)"
        :class="{ hidden: activeCard === i }"
        :data-index="i"
        @click="onClick"
        uc-word-skip=""
        data-sfx="blinds-open"
      )
</template>

<script setup lang="ts">
import {
  breakpointsTailwind,
  useBreakpoints,
  useEventListener
} from '@vueuse/core';
import {
  ref,
  nextTick,
  onMounted,
  inject,
  watch,
  toRef,
  withDefaults
} from 'vue';
import { getCompleted, setCompleted, set } from '../lib/SCORM';
import useSuspend from '../lib/useSuspend';
import { play, stop, queue } from '../lib/audio';
import useTranslations from '../stores/useTranslations';
import isQuizPage from '../lib/isQuizPage';
import useScreenAudio from '../lib/useScreenAudio';
import blindsSchema from '../lib/screen-schemas/screen-blinds.schema';

type Card = UcScreen & { panel?: boolean };

const suspend = useSuspend();
const breakpoints = useBreakpoints(breakpointsTailwind);
const largeDevice = breakpoints.greaterOrEqual('lg');
const { translations } = useTranslations();

const pageId = inject('pageId') as object;
const sectionActive = inject('sectionActive');
const wordDoc = inject('wordDoc') as boolean;
const sectionId = inject('sectionId') as string;
const audioDoc = inject('audioDoc') as boolean;

const props = withDefaults(
  defineProps<{
    editPath: string;
    screen: UcScreen & {
      cards?: Card[];
    };
    /** Ancestor owns section intro when nested under `uc-screen` / `templates[]`. */
    nested?: boolean;
  }>(),
  { nested: false }
);

const activeCard = ref(0);
const getCardId = (i: number | string) => `${props.screen.id}-blind-${i}`;

const { playSlot, queueSlot } = useScreenAudio(
  toRef(props, 'screen'),
  blindsSchema
);

const areAllCompleted = () => {
  const id = props.screen.id;
  let allComplete = true;
  let i = props.screen.cards.length;

  while (i-- && allComplete) {
    const cardId = getCardId(i);

    if (!suspend.completed[cardId]) {
      allComplete = false;
    }
  }

  return allComplete;
};

const onClick = async (e: MouseEvent) => {
  const target = e.currentTarget as HTMLDivElement;
  const i = +target.dataset.index;
  activeCard.value = i;

  stop();

  const card = props.screen.cards?.[i] as UcContent | undefined;
  const propAudio = card?.audio || card?.speech;
  if (propAudio) {
    play(propAudio);
  } else {
    playSlot(`blind-${i}`);
  }

  setCompleted(getCardId(i));

  await nextTick();

  if (areAllCompleted()) {
    setCompleted(props.screen.id);
    setCompleted(sectionId);
  }
};

const completeOnMobile = () => {
  if (
    sectionActive.value &&
    !largeDevice.value &&
    !getCompleted(props.screen.id)
  ) {
    props.screen?.cards?.forEach((card, i) => {
      setCompleted(getCardId(Number(i)));
    });
    setCompleted(props.screen.id);
    setCompleted(sectionId);
  }
};

const ariaLabel = (i: number) => {
  const translation = hasTranslation('Open card ${i} of ${n}');

  return translation
    .replace('${i}', String(i + 1))
    .replace('${n}', String(props.screen?.cards?.length));
};

function hasTranslation(key: string) {
  // get the translation if it exists
  return translations?.[suspend.locale]?.[key] || key;
}

onMounted(async () => {
  setCompleted(getCardId(0));

  // The first section on a page never receives a false→true sectionActive
  // transition, so play its intro here (mirrors uc-screen.vue).
  await nextTick();
  if (!props.nested && sectionActive.value) void playSectionAudio();
});

// when the screen becomes active: auto-complete on mobile, then run schema audio sequence.
// Queue the currently active card's audio after the intro.
const playSectionAudio = async () => {
  completeOnMobile();
  await playSlot('intro');
  const i = activeCard.value;
  const card = props.screen.cards?.[i] as UcContent | undefined;
  const propAudio = card?.audio || card?.speech;
  if (propAudio) {
    queue(propAudio, { owner: props.screen.id });
  } else {
    queueSlot(`blind-${i}`);
  }
};

watch(sectionActive, active => {
  if (props.nested || !active) return;
  void playSectionAudio();
});

useEventListener(window, 'resize', completeOnMobile);

const app = document.querySelector('#app');
app?.addEventListener('quizReset', async e => {
  if (await isQuizPage(e, pageId)) {
    props.screen?.cards?.forEach((card, i) => {
      set(`completed.${getCardId(Number(i))}`, false);
    });
  }
  activeCard.value = 0;
  setCompleted(getCardId(0));
});
</script>
